import { useTranslation } from "react-i18next";
import { activeFilterChip } from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { campaignStatusLabelKey } from "./campaign-report-presentation.js";
import type { ReportCampaignOptionsResponse } from "@multi-chat/shared";
import {
  CampaignReportFilters,
  type CampaignReportFilterValues,
} from "./CampaignReportFilters.js";
import type { CampaignReportQuery } from "./campaign-report-query.js";
import { useReportFiltersSheet } from "./ReportFilterSheet.js";
import { ReportToolbarRow } from "./ReportToolbarRow.js";

export function countCampaignActiveFilters(values: CampaignReportFilterValues): number {
  let count = 0;
  if (values.status) count += 1;
  if (values.endpointId != null) count += 1;
  if (values.template) count += 1;
  return count;
}

export function CampaignReportToolbar({
  query,
  dateFrom,
  dateTo,
  options,
  filteredTotal,
  filtersActive,
  onSearchChange,
  onDateRangeChange,
  onFiltersChange,
  onResetFilters,
  columnsSlot,
}: {
  query: CampaignReportQuery;
  dateFrom: string;
  dateTo: string;
  options: ReportCampaignOptionsResponse | null;
  filteredTotal: number | null;
  filtersActive: boolean;
  onSearchChange: (value: string) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onFiltersChange: (patch: Partial<CampaignReportFilterValues>) => void;
  onResetFilters: () => void;
  columnsSlot?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { filtersOpen, setFiltersOpen } = useReportFiltersSheet();

  const filterValues: CampaignReportFilterValues = {
    status: query.status,
    endpointId: query.endpointId,
    template: query.template,
  };
  const activeCount = countCampaignActiveFilters(filterValues);
  const sender = options?.senders.find((item) => item.endpointId === query.endpointId);
  const filterChips = [
    activeFilterChip(
      "status",
      t("reports.campaigns.filters.status"),
      query.status ? t(campaignStatusLabelKey(query.status)) : null,
      () => onFiltersChange({ status: undefined }),
    ),
    activeFilterChip(
      "sender",
      t("reports.campaigns.filters.sender"),
      sender ? (sender.displayName ?? sender.phoneNumber) : null,
      () => onFiltersChange({ endpointId: undefined }),
    ),
    activeFilterChip(
      "template",
      t("reports.campaigns.filters.template"),
      query.template,
      () => onFiltersChange({ template: undefined }),
    ),
  ].flatMap((chip) => (chip ? [chip] : []));

  const filters = (
    <CampaignReportFilters
      options={options}
      values={filterValues}
      onChange={onFiltersChange}
      idPrefix="reports-campaigns-more"
    />
  );

  return (
    <ReportToolbarRow
      toolbarTestId="reports-campaigns-toolbar"
      search={query.search}
      onSearchChange={onSearchChange}
      searchPlaceholder={t("reports.campaigns.searchPlaceholder")}
      searchTestId="reports-campaigns-search"
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateRangeChange={onDateRangeChange}
      fromTestId="reports-campaigns-from"
      toTestId="reports-campaigns-to"
      columnsSlot={columnsSlot}
      moreFilters={filters}
      moreFiltersCount={activeCount}
      moreFiltersTestId="reports-campaigns-more-filters"
      mobileFiltersOpen={filtersOpen}
      onMobileFiltersOpenChange={setFiltersOpen}
      mobileFiltersTitle={t("reports.campaigns.filtersTitle")}
      mobileFiltersDescription={t("reports.campaigns.filtersDescription")}
      mobileFiltersTestId="reports-campaigns-filters-sheet"
      mobileFiltersOpenTestId="reports-campaigns-open-filters"
      mobileFiltersBody={
        <CampaignReportFilters
          options={options}
          values={filterValues}
          onChange={onFiltersChange}
          idPrefix="reports-campaigns-mobile"
        />
      }
      filtersActive={filtersActive}
      explicitDateFrom={query.from}
      explicitDateTo={query.to}
      activeFilters={filterChips}
      onResetFilters={onResetFilters}
      resetTestId="reports-campaigns-reset-filters"
      filteredCountSlot={
        filteredTotal != null ? (
          <p
            className="m-0 flex h-8 shrink-0 items-center whitespace-nowrap text-[0.75rem] text-muted"
            data-testid="reports-campaigns-filtered-count"
          >
            {t("reports.campaigns.filteredCount", {
              formatted: new Intl.NumberFormat(i18n.language).format(filteredTotal),
            })}
          </p>
        ) : null
      }
    />
  );
}
